import { Button, Tabs } from 'antd';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import EditProfile from './EditProfile/EditProfile';
import FormPopup from '../../components/Popup/FormPopup';
import Icon from '../../components/Icon/Icon';
import ProfileCard from '../../components/Cards/ProfileCard';
import UserAverageRating from '../../components/UserAverageRating/UserAverageRating';
import UserSettings from './Settings/UserSettings';

import useEditCurrentProfile from './useEditCurrentProfile';
import useCurrentProfile from './EditProfile/useEditProfile';
import UploadImage from "../../components/UploadImage/UploadImage";

import styles from './EditCurrentProfile.module.scss';


function useQuery() {
	return new URLSearchParams(useLocation().search);
}

const EditCurrentProfile = () => {
	
	const { TabPane } = Tabs;

	const tab = useQuery().get('tab');
	let { url } = useRouteMatch();

	const { updateProfile } = useCurrentProfile();

	const {
		activeTab,
		editImage,
		getI18nLanguageName,
		isAuthorized,
		navigateTo,
		onChangeTab,
		setCurrentUser,
		state,
		toggleImagePopup,
		toggleUpgradeProfileForm,
		upgradeProfile,
		upgradeProfileFormActions,
		upgradeProfileFormFields
	} = useEditCurrentProfile();

	const { averageScore, currentUser, showUpgradeProfileForm } = state;

	const defaultValues: any = {
		conference: currentUser?.conference?.id,
		currentPosition: currentUser?.currentPosition,
		firstName: currentUser?.firstName,
		introduction: currentUser?.introduction,
		isReachableByCall: currentUser?.isReachableByCall,
		isReachableByText: currentUser?.isReachableByText,
		languages: currentUser?.languages,
		lastName: currentUser?.lastName,
		location: currentUser?.location,
		mentoringScopes: currentUser?.mentoringScopes,
		pronouns: currentUser?.pronouns,
		researchAreas: currentUser?.researchAreas,
		title: currentUser?.title,
	};

	const {
		control,
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
	} = useForm({ defaultValues });

	useEffect(() => {
		reset(defaultValues)
	}, [currentUser]);

	useEffect(() => {
		if (tab === 'settings') onChangeTab('2');
		else onChangeTab('1');
	}, [url]);

	const resetForm = () => {
		reset(defaultValues);
		navigateTo('/my-profile');
	};

	const tabBarExtraContent = {
		left: (
			<span className={styles.tabBarExtraContentLeft}>
				{currentUser?.firstName} {currentUser?.lastName}{' '}
			</span>
		),
		right: (
			<div className={`${styles.tabBarExtraContent}`}>
				<Button size="large" onClick={resetForm}>
					<FormattedMessage id="buttons.cancel" />
				</Button>
				{activeTab === '1' && <Button
					size="large"
					type="primary"
					onClick={handleSubmit(updateProfile)}
				>
					{' '}
					<FormattedMessage id="buttons.save" />
				</Button>}
			</div>
		)
	};

	const renderProfileCard = () => (
		<ProfileCard
			actions={[
				<div className={styles.actions}>
					{isAuthorized(['RequestUpgradeRole']) && currentUser?.canRequestUpgradeRole && (
						<Button className={styles.becomeMentorButton} onClick={toggleUpgradeProfileForm}><FormattedMessage id="buttons.becomeMentor"/></Button>
					)}
					{isAuthorized(['RequestUpgradeRole']) && !currentUser?.canRequestUpgradeRole && (
						<Button className={styles.becomeMentorButton} disabled><FormattedMessage id="buttons.pendingUpgradeRequest"/></Button>
					)}
					{averageScore && (
						<UserAverageRating score={averageScore} isCurrentUser={true} />
					)}
					<div className={styles.availability}>
						<h3>
							<FormattedMessage id="userProfile.availableFor" />
							<Icon color="text" height="8" name="chevron-right" />
						</h3>
						<div className={styles.iconsWrapper}>
							{currentUser?.isReachableByText ? (
								<Icon color="green" name="comment-alt-lines" type="solid" />
							) : (
								<Icon color="light" name="comment-alt-slash" type="solid" />
							)}
							{currentUser?.isReachableByCall ? (
								<>
									<Icon color="green" name="phone" type="solid" />
									<Icon color="green" name="video" type="solid" />
								</>
							) : (
								<>
									<Icon color="light" name="phone-slash" type="solid" />
									<Icon color="light" name="video-slash" type="solid" />
								</>
							)}
						</div>
					</div>
				</div>,
			]}
			fitContainer
			interactiveImage
			onClick={toggleImagePopup}
			showData
			data={{
				currentPosition: currentUser?.currentPosition,
				education: currentUser?.education,
				imageId: currentUser?.imageId,
				languages: currentUser?.languages?.map((item: string) => getI18nLanguageName(item)),
				location: currentUser?.location,
				name: `${currentUser?.firstName} ${currentUser?.lastName}`,
				pronouns: currentUser?.pronouns,
				researchAreas: currentUser?.researchAreas,
				workExperience: currentUser?.workExperience,
			}}
		/>
	);

	return (
		<div className={styles.pageContainer}>
			<div
				className={`${styles.pageSider} ${styles.topBorder} ${styles.noPadding}`}
			>
				{currentUser && renderProfileCard()}
			</div>
			<div className={styles.pageContent}>
				<div
					className={`${styles.mobileTopper} ${styles.topBorder} ${styles.noPadding}`}
				>
					{currentUser && renderProfileCard()}
				</div>
				<div className={styles.tabBarExtraContentMobile}>
					<Button size="large" onClick={resetForm}>
						<FormattedMessage id="buttons.cancel" />
					</Button>
					{activeTab === '1' && (
						<Button
							size="large"
							type="primary"
							onClick={handleSubmit(updateProfile)}
						>
							{' '}
							<FormattedMessage id="buttons.save" />
						</Button>
					)}
				</div>
				{currentUser && (
					<div className={styles.content}>
						<Tabs
							activeKey={activeTab}
							className="navigator"
							defaultActiveKey={tab === 'settings' ? '2' : '1'}
							tabBarExtraContent={tabBarExtraContent}
							onChange={onChangeTab}
						>
							<TabPane
								tab={
									<>
										<span className={styles.tabNav}>
											<FormattedMessage id="navigation.setupProfile" />
										</span>
										<span className={styles.tabDivider}>/</span>
									</>
								}
								key="1"
							>
								<EditProfile
									control={control}
									errors={errors}
								/>
							</TabPane>
							<TabPane
								tab={
									<>
										<span className={styles.tabNav}>
											<FormattedMessage id="navigation.userSettings" />
										</span>
									</>
								}
								key="2"
							>
								<UserSettings />
							</TabPane>
						</Tabs>
					</div>
				)}
			</div>

			{showUpgradeProfileForm && (
				<FormPopup
					actions={upgradeProfileFormActions}
					title={{ action: 'upgrade', target: 'profile' }}
					fields={upgradeProfileFormFields}
					onCancel={toggleUpgradeProfileForm}
					onSubmitForm={upgradeProfile}
				/>
			)}
			{editImage && (
				<UploadImage
					onClose={toggleImagePopup}
					currentUser={currentUser}
					afterClosed={(data) => setCurrentUser(data)}
				/>
			)}
		</div>
	);
};
export default EditCurrentProfile;